// The 13 real shipped projects. Facts come from the original portfolio data:
// name, one-line description, stack (only where it was listed), Play Store
// package (only where one exists) and local screenshots (only where one exists).
//
// "context" and "features" are written conservatively from the app category:
// they describe what an app of this type does, not invented metrics or clients.

const PUBLIC = process.env.PUBLIC_URL || "";
const img = (file) => `${PUBLIC}/images/portfolio/${file}`;
const play = (pkg) =>
  pkg ? `https://play.google.com/store/apps/details?id=${pkg}` : null;

const raw = [
  {
    slug: "brandmax",
    name: "BrandMax",
    tagline: "Wholesale shoe ordering app",
    category: "B2B Commerce",
    featured: true,
    stack: ["React Native", "Node.js", "Express.js", "JavaScript", "AWS", "Figma"],
    playPackage: "com.Footwear",
    image: img("Brandmax.jpg"),
    imageAlt:
      "Collage of BrandMax mobile app screens showing wholesale footwear catalogue and ordering flows",
    overview:
      "BrandMax is a wholesale shoe application that lets trade buyers browse a footwear catalogue and place bulk orders from their phone.",
    context:
      "Wholesale footwear ordering involves large catalogues, many size and style variants, and repeat bulk purchases. A dedicated mobile app gives buyers a faster ordering channel than manual, offline processes.",
    role:
      "Designed the interface in Figma, built the React Native app, and developed the Node.js and Express backend deployed on AWS.",
    features: [
      "Product catalogue organised for wholesale browsing",
      "Bulk ordering flow for trade buyers",
      "Node.js and Express API serving catalogue and order data",
      "Backend services hosted on AWS",
      "UI designed end to end in Figma before implementation",
    ],
  },
  {
    slug: "farmers-harvest",
    name: "Farmers Harvest",
    tagline: "Grocery shopping app",
    category: "Grocery",
    featured: true,
    stack: ["React Native", "JavaScript", "Figma"],
    playPackage: "com.tharaspices.farmersharvest",
    image: img("farmersharvest.jpg"),
    imageAlt:
      "Farmers Harvest grocery app screens showing product listings and shopping flows",
    overview:
      "Farmers Harvest is a grocery app for browsing products and ordering from a mobile storefront.",
    context:
      "Grocery customers expect to find everyday items quickly, add them to a basket and check out in a few taps. The app brings the store catalogue to mobile.",
    role: "Designed the screens in Figma and built the mobile app in React Native.",
    features: [
      "Category-based grocery browsing",
      "Product listings with pricing",
      "Cart and checkout flow",
      "Order history for repeat purchases",
    ],
  },
  {
    slug: "hyr-online",
    name: "Hyr Online",
    tagline: "Retail and rentals marketplace app",
    category: "Retail",
    playPackage: "com.avanexa.hyr",
    image: img("hyr.jpg"),
    imageAlt:
      "Hyr Online app screens showing category browsing, vehicle listings, business profiles and reviews",
    overview:
      "Hyr Online is a retail app where users browse listings by category and contact sellers and service providers directly.",
    context:
      "Local listings are scattered across many channels. A single app organises them by category and location and makes it easy to reach the provider.",
    role: "Mobile application development.",
    features: [
      "Category and location-based browsing",
      "Listing detail pages with pricing information",
      "Business profiles with ratings and reviews",
      "One-tap call and WhatsApp contact actions",
    ],
  },
  {
    slug: "voosh-10min",
    name: "Voosh 10min",
    tagline: "Quick food delivery app",
    category: "Food Delivery",
    image: img("Voosh.webp"),
    imageAlt: "Voosh 10min food delivery app screens showing menu and ordering flows",
    overview:
      "Voosh 10min is a food delivery app built around fast ordering and short delivery times.",
    context:
      "Quick delivery depends on a short path from opening the app to placing an order, with clear status updates afterwards.",
    role: "Mobile application development.",
    features: [
      "Menu browsing and item selection",
      "Cart and checkout",
      "Order placement and status tracking",
      "Delivery address management",
    ],
  },
  {
    slug: "meri-saheli",
    name: "Meri Saheli",
    tagline: "Grocery and home appliances app",
    category: "Commerce",
    playPackage: "com.saheliapp",
    image: img("Meri_Saheli.jpg"),
    imageAlt:
      "Meri Saheli app screens showing grocery and home appliance product catalogues",
    overview:
      "Meri Saheli is a shopping app that sells groceries alongside home appliances in one catalogue.",
    context:
      "Everyday groceries and larger home appliance purchases have very different browsing needs, and both have to fit in one storefront.",
    role: "Mobile application development.",
    features: [
      "Unified catalogue for groceries and appliances",
      "Category navigation and product detail pages",
      "Cart and order placement",
      "Order tracking",
    ],
  },
  {
    slug: "saj-surveyors",
    name: "SAJ Surveyors",
    tagline: "Survey document download tool",
    category: "Utility",
    overview:
      "SAJ Surveyors is a mobile tool for accessing and downloading land survey documents.",
    context:
      "Survey documents are usually requested and delivered by hand. A mobile tool lets users find and download them directly.",
    role: "Mobile application development.",
    features: [
      "Look up survey documents",
      "Download documents to the device",
      "Simple, task-focused interface",
    ],
  },
  {
    slug: "truck-taxi-driver",
    name: "Truck Taxi Driver",
    tagline: "Truck cab booking service",
    category: "Logistics",
    playPackage: "com.Truck_Taxi",
    overview:
      "Truck Taxi Driver is the driver-side app for a truck cab booking service.",
    context:
      "Goods transport bookings have to reach available drivers quickly, and drivers need a clear view of each trip.",
    role: "Mobile application development.",
    features: [
      "Receive and manage booking requests",
      "Trip details for pickup and drop-off",
      "Map-based location information",
      "Trip history",
    ],
  },
  {
    slug: "wall360",
    name: "Wall360",
    tagline: "Real estate platform for buying and renting",
    category: "Real Estate",
    confidential: true,
    featured: true,
    playPackage: "com.wall360",
    image: img("Albion.jpg"),
    imageAlt:
      "Real estate app screens showing property search, buy and rent listings, PG listings and a home loan screen",
    overview:
      "Wall360 is a real estate platform (built for a confidential client) where users find properties to buy or rent.",
    context:
      "Property seekers compare many listings across budget, type and locality. The app brings buy, rent and commercial listings together with filters and direct contact.",
    role: "Mobile application development.",
    features: [
      "Buy, rent and commercial listing categories",
      "Filters for budget, property type and locality",
      "Property detail pages with pricing",
      "Direct call and chat actions to reach owners",
      "Wishlist and account area",
    ],
  },
  {
    slug: "bank-auctions",
    name: "Bank Auctions",
    tagline: "Bank auction properties to buy or rent",
    category: "Real Estate",
    playPackage: "com.albionbankauctions",
    overview:
      "Bank Auctions is an app for discovering properties offered through bank auctions, for purchase or rent.",
    context:
      "Bank auction listings are hard to track down. A dedicated app gathers them in one place.",
    role: "Mobile application development.",
    features: [
      "Browse auction property listings",
      "Property detail pages",
      "Search and filter listings",
      "Save listings of interest",
    ],
  },
  {
    slug: "credit360",
    name: "Credit360",
    tagline: "Credit score and loan app",
    category: "Fintech",
    confidential: true,
    featured: true,
    playPackage: "com.albioncredit",
    image: img("credit-portfolio.jpg"),
    imageAlt:
      "Financial credit tracker app screens showing credit score and loan application flows",
    overview:
      "Credit360 is a financial credit tracker (built for a confidential client) that shows users their credit score and offers loans.",
    context:
      "Borrowers want to understand their credit standing before they apply for a loan. The app puts both steps in one place.",
    role: "Mobile application development.",
    features: [
      "Credit score overview",
      "Loan offerings and application flow",
      "User onboarding and profile",
      "Secure account access",
    ],
  },
  {
    slug: "fobes",
    name: "Fobes",
    tagline: "Job portal app for candidates",
    category: "Hiring",
    playPackage: "com.fobesjobportal",
    image: img("fobes_candidate.jpg"),
    imageAlt: "Fobes job portal app screens showing job listings and candidate profile",
    overview: "Fobes is a job portal application where candidates discover and apply for jobs.",
    context:
      "Job seekers need to search openings, keep a profile up to date and track their applications from their phone.",
    role: "Mobile application development.",
    features: [
      "Job search and listings",
      "Candidate profile management",
      "Apply to jobs from the app",
      "Application tracking",
    ],
  },
  {
    slug: "fobes-employer",
    name: "Fobes Employer",
    tagline: "Employer-side job portal app",
    category: "Hiring",
    overview:
      "Fobes Employer is the employer companion to the Fobes job portal, used to post jobs and manage applicants.",
    context:
      "A two-sided job portal needs a separate experience for employers: posting roles and reviewing the candidates who apply.",
    role: "Mobile application development.",
    features: [
      "Post and manage job openings",
      "Review candidate applications",
      "Company profile management",
    ],
  },
  {
    slug: "shopeasey",
    name: "ShopEasey",
    tagline: "Online shopping app",
    category: "E-commerce",
    confidential: true,
    playPackage: "com.shopeasey",
    overview:
      "ShopEasey is an e-commerce app (built for a confidential client) for shopping online from a mobile storefront.",
    context:
      "Online shoppers expect smooth product discovery, a reliable cart and a simple checkout on mobile.",
    role: "Mobile application development.",
    features: [
      "Product catalogue with categories",
      "Product detail pages",
      "Cart and checkout flow",
      "Order history",
    ],
  },
  {
    slug: "soft",
    name: "Soft",
    tagline: "Sales reporting app — sales at your fingertips",
    category: "Sales & Reporting",
    featured: true,
    stack: ["React Native", "JavaScript", "Figma"],
    playPackage: "com.softewp",
    overview:
      "Soft is a sales reporting application that gives sales teams a single view of their sales reports from their phone.",
    context:
      "Sales teams need up-to-date numbers without waiting on a desktop report. Soft puts the sales data they'd otherwise chase down in spreadsheets directly into a mobile app.",
    role: "Designed the interface in Figma and built the React Native app.",
    features: [
      "Consolidated sales report view",
      "Fast access to figures from a mobile device",
      "Report browsing organised for sales teams",
    ],
  },
  {
    slug: "wallposter",
    name: "Wallposter",
    tagline: "Local advertising app for businesses and customers",
    category: "Advertising",
    stack: ["React Native", "JavaScript", "Figma"],
    inProgress: true,
    overview:
      "Wallposter is an advertising app where business owners post product offers and customers browse nearby offers and visit the business to buy.",
    context:
      "Small businesses often advertise offers informally, with no easy way for nearby customers to discover them. Wallposter gives owners a place to post offers and gives customers a way to find them.",
    role: "Designed the interface in Figma and building the React Native app.",
    features: [
      "Business owner offer/advertisement posting",
      "Customer-facing offer browsing",
      "Location-relevant advertisement discovery",
    ],
  },
  {
    slug: "arja-customer",
    name: "Arja Customer",
    tagline: "Solar panel maintenance request app",
    category: "Solar Maintenance",
    stack: ["React Native", "JavaScript", "Figma"],
    playPackage: "com.arja.customer",
    overview:
      "Arja Customer is a solar panel maintenance app where customers report a problem with their installation and Arja resolves it.",
    context:
      "Solar panel owners usually report faults over a phone call with no record of the issue or its resolution. Arja Customer gives them a direct way to log the problem and track it through to a fix.",
    role: "Designed the interface in Figma and built the React Native app.",
    features: [
      "Customer-reported solar panel issue logging",
      "Request status tracking through to resolution",
      "Customer account and installation profile",
      "Notifications on request status",
    ],
  },
  {
    slug: "arja-hrms",
    name: "Arja HRMS",
    tagline: "Workforce tracking app for solar maintenance staff",
    category: "Solar Maintenance",
    stack: ["React Native", "JavaScript", "Figma"],
    playPackage: "com.arja.hrms",
    overview:
      "Arja HRMS is the internal workforce app for Arja, used to track the technicians who resolve the solar panel maintenance requests customers submit.",
    context:
      "Once a customer logs a maintenance problem, it has to be assigned to and tracked through a technician. Arja HRMS gives the business a mobile way to manage that staff-side workflow rather than on paper or spreadsheets.",
    role: "Designed the interface in Figma and built the React Native app.",
    features: [
      "Technician assignment against customer requests",
      "Staff attendance and activity tracking",
      "Staff directory and profiles",
      "Manager-facing monitoring views",
    ],
  },
  {
    slug: "privets",
    name: "Privets",
    tagline: "IoT app to control motors remotely",
    category: "IoT",
    stack: ["React Native", "JavaScript", "Figma"],
    playPackage: "com.privets.customer",
    overview:
      "Privets is an IoT app that lets users switch a motor on or off and control it directly from their phone.",
    context:
      "Motors are often controlled with a physical switch on-site. Privets connects the motor to the user's phone so it can be turned on or off remotely.",
    role: "Designed the interface in Figma and built the React Native app.",
    features: [
      "Remote motor on/off control",
      "Device connection and status",
      "Simple, single-purpose control interface",
    ],
  },
  {
    slug: "airavad-chat",
    name: "Airavad Chat",
    tagline: "Business chat application",
    category: "Business Communication",
    stack: ["React Native", "JavaScript", "Figma"],
    playPackage: "com.airone.airavadchat",
    overview:
      "Airavad Chat is a business chat application built to provide messaging services as part of the Airavad/AirOne product suite.",
    context:
      "Business communication needs a dedicated channel separate from personal messaging apps, with conversations scoped to the business.",
    role: "Designed the interface in Figma and built the React Native app.",
    features: [
      "One-to-one business messaging",
      "Conversation and contact management",
      "Real-time message delivery",
    ],
  },
  {
    slug: "newline-hr",
    name: "Newline HR",
    tagline: "HR product to monitor employees",
    category: "HR Tech",
    stack: ["React Native", "JavaScript", "Figma"],
    overview:
      "Newline HR is a human resource management app used to monitor employees.",
    context:
      "Like other HR tooling, Newline HR moves employee monitoring from manual processes onto a mobile app for HR teams and managers.",
    role: "Designed the interface in Figma and built the React Native app.",
    features: [
      "Employee monitoring and activity tracking",
      "HR-facing staff overview",
      "Mobile access for managers",
    ],
  },
  {
    slug: "emediexp",
    name: "EMediExp",
    tagline: "Medical prescription and billing app",
    category: "Healthcare",
    stack: ["React Native", "JavaScript", "Figma"],
    overview:
      "EMediExp is a medical prescription application connecting patients, doctors and pharmacists for prescribing and billing.",
    context:
      "Prescribing and billing usually happen on paper across three separate parties. EMediExp gives doctors, pharmacists and patients a shared, mobile-first flow for prescriptions and billing.",
    role: "Designed the interface in Figma and built the React Native app.",
    features: [
      "Doctor prescription creation",
      "Pharmacist prescription fulfilment and billing",
      "Patient-facing prescription and billing view",
    ],
  },
  {
    slug: "el-alimentes",
    name: "El-Alimentes",
    tagline: "Food ordering app for customers and drivers",
    category: "Food Delivery",
    stack: ["React Native", "JavaScript", "Figma"],
    overview:
      "El-Alimentes is a food ordering application with separate customer and driver apps.",
    context:
      "Food ordering needs two connected experiences: customers placing and tracking orders, and drivers receiving and fulfilling deliveries.",
    role: "Designed the interface in Figma and built the React Native customer and driver apps.",
    features: [
      "Customer menu browsing and ordering",
      "Cart and checkout flow",
      "Driver-side delivery assignment and tracking",
      "Order status updates for both apps",
    ],
  },
  {
    slug: "sunwin-namkeen",
    name: "Sunwin Namkeen",
    tagline: "Namkeen product website",
    category: "E-commerce",
    featured: true,
    stack: ["Next.js", "Node.js", "Prisma", "PostgreSQL"],
    liveUrl: "https://sunwin.szvp2024.com/",
    liveLabel: "Visit website",
    overview:
      "Sunwin Namkeen is a website for a namkeen (savoury snacks) business, built to showcase and sell its products online.",
    context:
      "A snacks business needs a web storefront where its product range is presented clearly and can be browsed or ordered from any device.",
    role: "Built the Next.js frontend and the Node.js, Prisma and PostgreSQL backend.",
    features: [
      "Product catalogue for the namkeen range",
      "Next.js frontend with a PostgreSQL-backed product database",
      "Prisma ORM data layer over PostgreSQL",
    ],
  },
  {
    slug: "ewr-plot-management",
    name: "EWR Plot Management",
    tagline: "Real estate plot management platform",
    category: "Real Estate",
    featured: true,
    stack: ["Next.js", "Node.js", "Prisma", "PostgreSQL"],
    liveUrl: "https://admin.eastwestrealty.in/auth/sign-in",
    liveLabel: "Visit admin platform",
    overview:
      "EWR Plot Management is an admin platform for managing real estate plot sales, including CAD plot layouts and per-plot status.",
    context:
      "Plot-based real estate sales need to track which plots are sold, booked or available against a visual layout, not just a spreadsheet of numbers.",
    role: "Built the Next.js frontend and the Node.js, Prisma and PostgreSQL backend.",
    features: [
      "CAD plot layout viewer",
      "Per-plot selection with sold/booked/available status",
      "Plot sales tracking and management dashboard",
      "Admin authentication",
    ],
  },
];

export const projects = raw.map((p, index) => ({
  ...p,
  index,
  stack: p.stack || [],
  role: p.role || "Mobile Application Developer",
  playUrl: play(p.playPackage),
}));

export const featuredProjects = projects.filter((p) => p.featured);

export const getProject = (slug) => projects.find((p) => p.slug === slug);
